import { Label } from '@/components/ui';
import { cn } from '@/helpers/ui-utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { RadioGroup, RadioGroupItem } from './radio-group';

const radioItemVariants = cva(
  [
    'outline-hidden',
    'h-4',
    'w-4',
    'p-0',
    'rounded-full',
    'border',
    'border-solid',
    'border-[#D4D4D4]',
    'bg-transparent',
    'transition-all',
    'duration-300',
    'ease-in-out',
    'data-[state=checked]:border-primary',
    'hover:border-[#A3A3A3]',
    'hover:cursor-pointer',
    'focus:ring-2',
    'focus:ring-[#D4D4D4]',
    'focus:data-[state=checked]:ring-[#E6ECFF]',
  ],
  {
    variants: {
      disabled: {
        true: ['opacity-50', 'hover:cursor-not-allowed', 'pointer-events-none'],
      },
      invalid: {
        true: ['ring', 'ring-2', 'ring-destructive'],
      },
    },
    defaultVariants: {
      disabled: false,
    },
  },
);

type Option = {
  value: string;
  label: string;
};

type Options = Option[];

export interface RadioGroupProps
  extends
    Omit<React.HTMLAttributes<HTMLInputElement>, 'className'>,
    VariantProps<typeof radioItemVariants> {
  /**
   * Array of options to display as radio buttons. Each option should have a unique value and descriptive label.
   * See {@link Option}
   */
  options: Options;

  /**
   * Layout direction for the radio group options.
   * - `vertical` - Stack options vertically (default)
   * - `horizontal` - Arrange options horizontally in a row
   * @default 'vertical'
   */
  orientation?: 'vertical' | 'horizontal';

  /**
   * The name attribute for the radio group. Used for form submission and grouping related radio buttons.
   */
  name: string;

  /**
   * The controlled value of the radio group. Use this when you want to control the selection programmatically.
   */
  value?: string;

  /**
   * Callback function called when the selected value changes. Receives the new selected value as a parameter.
   */
  onValueChange?: (value: string) => void;

  /**
   * Indicates if the radio group is in an invalid/error state. Applies warning styling to indicate validation errors.
   * @default false
   */
  invalid?: boolean;

  /**
   * Optional descriptive text displayed below the radio group. Useful for providing additional context or validation feedback.
   */
  helperText?: string | null;
}

const Group = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(
  (
    { orientation = 'vertical', disabled, helperText, invalid, ...props },
    ref,
  ) => {
    const itemStyles = radioItemVariants({ disabled, invalid });

    return (
      <RadioGroup
        name={props.name}
        defaultValue={`${props.defaultValue}`}
        value={props.value}
        ref={ref}
        orientation={orientation}
        onValueChange={props.onValueChange}
        className={cn({
          'grid-flow-row': orientation === 'vertical',
          'grid-flow-col': orientation === 'horizontal',
        })}
        disabled={disabled ?? undefined}
        onBlur={props.onBlur}
      >
        {props.options.map((option) => (
          <div className="flex items-center space-x-2" key={option.value}>
            <RadioGroupItem
              value={option.value}
              id={option.value}
              className={cn('peer', itemStyles)}
            />
            <Label
              htmlFor={option.value}
              className="hover:cursor-pointer peer-disabled:opacity-50"
            >
              {option.label}
            </Label>
          </div>
        ))}
        {helperText && (
          <span
            className={cn(['text-sm', 'text-foreground/50'], {
              'text-warning': invalid,
            })}
          >
            {helperText}
          </span>
        )}
      </RadioGroup>
    );
  },
);

export default Group;
